import NhlTeamStackingMarker from 'components/organisms/nhl-team-stacking/services/nhl-team-stacking-marker';
import {getSimplexUnitName, getPlayersSubGroupUID} from 'helpers/simplex-helper';
import Const from 'constants';
import {countEntries, getRandomArray} from 'helpers/array-helper';
import {calcAndRoundDownValByPct, calcAndRoundUpValByPct} from 'helpers/common-helper';
import RuleBuilder from 'services/simplex/rule-builder';
import {getCheckedNumbers} from '../helpers';

function getSubGroupName(teamName, comboId, stackNumber, subGroupKey, subGroupNumber) {
    const subGroupName = getPlayersSubGroupUID(teamName, comboId, stackNumber, subGroupKey, subGroupNumber);

    return NhlTeamStackingMarker.setSuffix(subGroupName);
}

function createDoubleTeamsConstraints(params) {
    const {constraints} = params;

    const result = [];

    if (isEmpty(constraints)) {
        return result;
    }

    const teamGroups = {};
    Object.keys(constraints).map((teamName) => {
        teamGroups[teamName] = constraints[teamName].subGroupNames;
    });

    Object.values(teamGroups).map((teamGroup) => {
        if (teamGroup.length >= 2) {
            const ruleBuilder = RuleBuilder.fromAtMost(1);
            teamGroup.map((subGroupName) => ruleBuilder.addVar(subGroupName, 1));

            result.push(ruleBuilder.createRule());
        }
    });

    return [...result];
}

function createConstraintsForExcludeTeamsCombinations(params) {
    const {lineupTeamsCombinations, rawLineups, lineupsQuantity, constraints} = params;
    const items = lineupTeamsCombinations.exposureMax.all();

    const comboTeamsConstraints = [];

    if (isEmpty(constraints)) {
        return comboTeamsConstraints;
    }

    Object.values(items).map((item) => {
        let usageQuantity = 0;
        rawLineups.asArr().map((lineup) => {
            const diff = arrayDiff(item.teamsCombination, lineup.getSubGroupTeamNames());

            if (isEmpty(diff)) {
                usageQuantity++;
            }
        });

        const expMax = item.value;
        const usagePercent = ((usageQuantity + 1) / lineupsQuantity.getLineupsQuantity()) * 100;

        if (usagePercent <= expMax) {
            return;
        }

        const [team1, team2, team3 = ''] = item.teamsCombination;

        const teamCombinationsMap = team3
            ? [
                  [team1, team2, team3],
                  [team1, team3, team2],
                  [team2, team1, team3],
                  [team2, team3, team1],
                  [team3, team1, team2],
                  [team3, team2, team1],
              ]
            : [
                  [team1, team2],
                  [team2, team1],
              ];

        teamCombinationsMap.map((teamCombination) => {
            const ruleBuilder = RuleBuilder.fromAtMost(teamCombination.length - 1);

            for (const teamName of teamCombination) {
                if (!(teamName in constraints)) {
                    return;
                }

                constraints[teamName].subGroupNames.map((subGroupName) => ruleBuilder.addVar(subGroupName, 1));
            }

            comboTeamsConstraints.push(ruleBuilder.createRule());
        });
    });

    return [...comboTeamsConstraints];
}

function createGroupsConstraints(params) {
    const {constraints, comboKey} = params;

    const stackUnitQuantityMap = {
        '2-2-2': {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 3,
        },
        '3-2-X': {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 1,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 1,
        },
        '3-2-2': {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 1,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 2,
        },
        '3-3-X': {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 2,
        },
        '3-3-2': {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 2,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 1,
        },
        '4-2-X': {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 1,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 1,
        },
        '4-2-2': {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 1,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 2,
        },
        '4-3-X': {
            [Const.NHL_TEAM_STACKING_FIRST_STACK]: 1,
            [Const.NHL_TEAM_STACKING_SECOND_STACK]: 1,
        },
    };

    const result = [];

    const subGroupNamesGroupedByStackNumbers = {};
    Object.values(constraints).map((teamConstraints) => {
        Object.keys(teamConstraints.subGroupNamesGroupedByStackNumbers).map((stackNumber) => {
            !(stackNumber in subGroupNamesGroupedByStackNumbers) &&
                (subGroupNamesGroupedByStackNumbers[stackNumber] = []);

            teamConstraints.subGroupNamesGroupedByStackNumbers[stackNumber].map((subGroupName) => {
                subGroupNamesGroupedByStackNumbers[stackNumber].push(subGroupName);
            });
        });
    });

    Object.keys(subGroupNamesGroupedByStackNumbers).map((stackNumber) => {
        const ruleBuilder = RuleBuilder.fromExactly(stackUnitQuantityMap[comboKey][stackNumber] || 1);
        subGroupNamesGroupedByStackNumbers[stackNumber].map((subGroupName) => ruleBuilder.addVar(subGroupName, 1));

        ruleBuilder.isRuleCreatable() && result.push(ruleBuilder.createRule());
    });

    return [...result];
}

function createConstraintsForIncludedTeamsByExposureMin(params) {
    const {constraints, lineupsQuantity, usedTeamNames, stacking} = params;

    const desirableConstraints = [];

    const takenTeamNames = [];
    Object.keys(constraints).map((teamName) => {
        const simplexVars = {};
        const expMin = stacking.getTeamMinExposureInStackNumber(teamName, 1);
        const reservedQuantity = calcAndRoundDownValByPct(lineupsQuantity.getLineupsQuantity(), expMin);
        const usageQuantity = countEntries(teamName, usedTeamNames);
        const canTeamBeTaken = 0 < reservedQuantity - usageQuantity && !inArray(teamName, takenTeamNames);

        if (canTeamBeTaken) {
            constraints[teamName].subGroupNames.map((subGroupName) => (simplexVars[subGroupName] = 1));
            takenTeamNames.push(teamName);
        }

        if (isEmpty(simplexVars)) {
            return;
        }

        const ruleBuilder = RuleBuilder.fromAtLeast(1);
        ruleBuilder.addVars(simplexVars);

        desirableConstraints.push(ruleBuilder.createRule());
    });

    return [...desirableConstraints];
}

function createObjectiveForRandomlyChosenPlayers(params) {
    const {playersGroupedByTeams, constraints, projectionPointsPropName} = params;

    const availableTeamNames = [...Object.keys(constraints)];
    const randomTeams = getRandomArray(availableTeamNames, 2);

    const result = {};
    randomTeams.map((teamName) => {
        const availablePlayers = playersGroupedByTeams[teamName];
        const randomPlayers = getRandomArray(availablePlayers, 3);

        randomPlayers.map((player) => {
            const pointsValue = player[projectionPointsPropName];
            const projectionPoints = pointsValue * Const.NHL_TEAM_STACKING_PROJECTION_MULTIPLY_VALUE;

            result[getSimplexUnitName(player.playerId)] = parseInt(projectionPoints);
        });
    });

    return {...result};
}

function createConstraintCombinations(params) {
    const {stacking, playersGroupedByTeams} = params;
    const combinations = stacking.getCheckedCombinations();
    const teams = stacking.getActiveAndCheckedTeamsByStackNumber(1);

    const teamSimplexUnits = {};
    Object.keys(teams).map((teamName) => {
        const {lineGroups, ppLines} = teams[teamName];
        const availableLineGroups = getCheckedNumbers(lineGroups);
        const availablePPLines = getCheckedNumbers(ppLines);

        if (isEmpty(availableLineGroups) && isEmpty(availablePPLines)) {
            return;
        }

        const lineGroupSimplexUnits = {};
        availableLineGroups.map((lineGroup) => {
            const simplexUnits = getSimplexTeamPlayerUnits(teamName, function (player) {
                return !player.isGoalie() && Number(player.userLineGroup) === lineGroup;
            });

            !isEmpty(simplexUnits) && (lineGroupSimplexUnits[lineGroup] = simplexUnits);
        });

        const ppLineSimplexUnits = {};
        availablePPLines.map((ppLine) => {
            const simplexUnits = getSimplexTeamPlayerUnits(teamName, function (player) {
                return !player.isGoalie() && Number(player.userPpLine) === ppLine;
            });

            !isEmpty(simplexUnits) && (ppLineSimplexUnits[ppLine] = simplexUnits);
        });

        if (isEmpty(lineGroupSimplexUnits) && isEmpty(ppLineSimplexUnits)) {
            return;
        }

        const defensemanLineGroupSimplexUnits = {};
        availableLineGroups.map((lineGroup) => {
            const simplexUnits = getSimplexTeamPlayerUnits(teamName, function (player) {
                return player.isDefenseman() && Number(player.userLineGroup) === lineGroup;
            });

            !isEmpty(simplexUnits) && (defensemanLineGroupSimplexUnits[lineGroup] = simplexUnits);
        });

        const defensemanPpLineSimplexUnits = {};
        availablePPLines.map((ppLine) => {
            const simplexUnits = getSimplexTeamPlayerUnits(teamName, function (player) {
                return player.isDefenseman() && Number(player.userPpLine) === ppLine;
            });

            !isEmpty(simplexUnits) && (defensemanPpLineSimplexUnits[ppLine] = simplexUnits);
        });

        const goalieSimplexUnits = getSimplexTeamPlayerUnits(teamName, (player) => player.isGoalie());

        teamSimplexUnits[teamName] = {
            lineGroupSimplexUnits,
            ppLineSimplexUnits,
            defensemanLineGroupSimplexUnits,
            defensemanPpLineSimplexUnits,
            goalieSimplexUnits,
        };
    });

    const comboTeamSimplexData = {};
    Object.keys(teamSimplexUnits).map((teamName) => {
        Object.keys(combinations).map((comboKey) => {
            const combo = combinations[comboKey];

            const teamStackMetadata = createTeamStackMetadata({stacking, combo, teamSimplexUnits, teamName});

            if (isEmpty(teamStackMetadata)) {
                return;
            }

            !(comboKey in comboTeamSimplexData) && (comboTeamSimplexData[comboKey] = {});

            const isDefensemanExcluded = !combo.isDefensemanMandatory && !teams[teamName].isDefensemanIncluded;

            let simplexUnits = teamSimplexUnits[teamName];
            if (isDefensemanExcluded) {
                const {
                    lineGroupSimplexUnits,
                    ppLineSimplexUnits,
                    defensemanLineGroupSimplexUnits,
                    defensemanPpLineSimplexUnits,
                } = teamSimplexUnits[teamName];

                const defensemanUnits = [];
                Object.values(defensemanLineGroupSimplexUnits).map((units) =>
                    units.map((unit) => defensemanUnits.push(unit)),
                );
                Object.values(defensemanPpLineSimplexUnits).map((units) =>
                    units.map((unit) => defensemanUnits.push(unit)),
                );

                const filteredLineGroupSimplexUnits = {};
                Object.keys(lineGroupSimplexUnits).map((key) => {
                    filteredLineGroupSimplexUnits[key] = lineGroupSimplexUnits[key].filter(
                        (unit) => !inArray(unit, defensemanUnits),
                    );
                });

                const filteredPpLineSimplexUnits = {};
                Object.keys(ppLineSimplexUnits).map((key) => {
                    filteredPpLineSimplexUnits[key] = ppLineSimplexUnits[key].filter(
                        (unit) => !inArray(unit, defensemanUnits),
                    );
                });

                simplexUnits = {
                    ...teamSimplexUnits[teamName],
                    lineGroupSimplexUnits: filteredLineGroupSimplexUnits,
                    ppLineSimplexUnits: filteredPpLineSimplexUnits,
                    defensemanLineGroupSimplexUnits: {},
                    defensemanPpLineSimplexUnits: {},
                };
            }

            comboTeamSimplexData[comboKey][teamName] = {
                teamName,
                simplexUnits,
                stackMetadata: teamStackMetadata,
            };
        });
    });

    const constraints = {};
    Object.keys(comboTeamSimplexData).map((comboKey) => {
        constraints[comboKey] = {};

        Object.keys(comboTeamSimplexData[comboKey]).map((teamName) => {
            const {simplexUnits, stackMetadata} = comboTeamSimplexData[comboKey][teamName];
            const {
                defensemanLineGroupSimplexUnits,
                defensemanPpLineSimplexUnits,
                lineGroupSimplexUnits,
                ppLineSimplexUnits,
                goalieSimplexUnits,
            } = simplexUnits;

            const subGroupNamesSet = new Set();
            const defensemanLineGroupConstraints = {};
            Object.keys(defensemanLineGroupSimplexUnits).map((lineGroup) => {
                const {constraints, subGroupNames} = createDefensemanConstraints({
                    defensemanTeamSimplexUnits: defensemanLineGroupSimplexUnits[lineGroup] || [],
                    teamSimplexUnits: lineGroupSimplexUnits[lineGroup] || [],
                    combo: combinations[comboKey],
                    stackMetadata,
                    groupName: 'lineGroupNames',
                    groupNumber: lineGroup,
                });

                if (isEmpty(constraints)) {
                    return;
                }

                defensemanLineGroupConstraints[lineGroup] = {lineGroup, constraints};

                subGroupNames.map((subGroupName) => subGroupNamesSet.add(subGroupName));
            });

            const defensemanPpLineConstraints = {};
            Object.keys(defensemanPpLineSimplexUnits).map((ppLine) => {
                const {constraints, subGroupNames} = createDefensemanConstraints({
                    defensemanTeamSimplexUnits: defensemanPpLineSimplexUnits[ppLine] || [],
                    teamSimplexUnits: ppLineSimplexUnits[ppLine] || [],
                    combo: combinations[comboKey],
                    stackMetadata,
                    groupName: 'ppLineNames',
                    groupNumber: ppLine,
                });

                if (isEmpty(constraints)) {
                    return;
                }

                defensemanPpLineConstraints[ppLine] = {ppLine, constraints};

                subGroupNames.map((subGroupName) => subGroupNamesSet.add(subGroupName));
            });

            const lineGroupConstraints = {};
            Object.keys(lineGroupSimplexUnits).map((lineGroup) => {
                const rowPlayersQuantity = lineGroupSimplexUnits[lineGroup].length;

                const simplexVars = {};
                Object.keys(stackMetadata).map((stackNumber) => {
                    const {playersQuantity, lineGroupNames} = stackMetadata[stackNumber];

                    if (rowPlayersQuantity < playersQuantity) {
                        return;
                    }

                    const isDefensemanMandatory = stacking.isDefensemanMandatoryInCombinationStack(
                        combinations[comboKey],
                        stackNumber,
                    );

                    if (isDefensemanMandatory && isEmpty(defensemanLineGroupSimplexUnits[lineGroup])) {
                        return;
                    }

                    simplexVars[lineGroupNames[lineGroup]] = {
                        objective: -1 * playersQuantity,
                        isDefensemanIncluded: isDefensemanMandatory || stacking.isTeamDefensemanIncluded(teamName),
                    };
                });

                if (isEmpty(simplexVars)) {
                    return;
                }

                lineGroupConstraints[lineGroup] = {
                    lineGroup,
                    constraints: [],
                };

                Object.keys(simplexVars).map((lineGroupSimplexUnit) => {
                    const {objective, isDefensemanIncluded} = simplexVars[lineGroupSimplexUnit];

                    const simplexUnits = isDefensemanIncluded
                        ? lineGroupSimplexUnits[lineGroup]
                        : lineGroupSimplexUnits[lineGroup].filter(
                              (simplexUnit) => !inArray(simplexUnit, defensemanLineGroupSimplexUnits[lineGroup] ?? []),
                          );

                    const builder = RuleBuilder.fromAtLeast(0);
                    simplexUnits.map((simplexUnit) => builder.addVar(simplexUnit, 1));
                    builder.addVar(lineGroupSimplexUnit, objective);

                    lineGroupConstraints[lineGroup].constraints.push(builder.createRule());
                });

                // const builder = RuleBuilder.fromAtLeast(0);
                // lineGroupSimplexUnits[lineGroup].map((simplexUnit) => builder.addVar(simplexUnit, 1));
                // builder.addVars(simplexVars);
                //
                // lineGroupConstraints[lineGroup] = {
                //     lineGroup,
                //     constraints: [builder.createRule()],
                // };

                Object.keys(simplexVars).map((subGroupName) => subGroupNamesSet.add(subGroupName));
            });

            const ppLineConstraints = {};
            Object.keys(ppLineSimplexUnits).map((ppLine) => {
                const rowPlayersQuantity = ppLineSimplexUnits[ppLine].length;

                const simplexVars = {};
                Object.keys(stackMetadata).map((stackNumber) => {
                    const {playersQuantity, ppLineNames} = stackMetadata[stackNumber];

                    if (rowPlayersQuantity < playersQuantity) {
                        return;
                    }

                    const isDefensemanMandatory = stacking.isDefensemanMandatoryInCombinationStack(
                        combinations[comboKey],
                        stackNumber,
                    );

                    if (isDefensemanMandatory && isEmpty(defensemanPpLineSimplexUnits[ppLine])) {
                        return;
                    }

                    simplexVars[ppLineNames[ppLine]] = {
                        objective: -1 * playersQuantity,
                        isDefensemanIncluded: isDefensemanMandatory || stacking.isTeamDefensemanIncluded(teamName),
                    };
                });

                if (isEmpty(simplexVars)) {
                    return;
                }

                ppLineConstraints[ppLine] = {
                    ppLine,
                    constraints: [],
                };

                Object.keys(simplexVars).map((ppLineSimplexUnit) => {
                    const {objective, isDefensemanIncluded} = simplexVars[ppLineSimplexUnit];

                    const simplexUnits = isDefensemanIncluded
                        ? ppLineSimplexUnits[ppLine]
                        : ppLineSimplexUnits[ppLine].filter(
                              (simplexUnit) => !inArray(simplexUnit, defensemanPpLineSimplexUnits[ppLine] ?? []),
                          );

                    const builder = RuleBuilder.fromAtLeast(0);
                    simplexUnits.map((simplexUnit) => builder.addVar(simplexUnit, 1));
                    builder.addVar(ppLineSimplexUnit, objective);

                    ppLineConstraints[ppLine].constraints.push(builder.createRule());
                });

                // const builder = RuleBuilder.fromAtLeast(0);
                // ppLineSimplexUnits[ppLine].map((simplexUnit) => builder.addVar(simplexUnit, 1));
                // builder.addVars(simplexVars);
                //
                // ppLineConstraints[ppLine] = {
                //     ppLine,
                //     constraints: [builder.createRule()],
                // };

                Object.keys(simplexVars).map((subGroupName) => subGroupNamesSet.add(subGroupName));
            });

            const goalieConstraint = createGoalieConstraint({
                goalieSimplexUnits,
                combo: combinations[comboKey],
                stackMetadata,
                subGroupNamesSet,
            });

            const subGroupNamesGroupedByStackNumbers = {};
            Object.keys(stackMetadata).map((stackNumber) => {
                subGroupNamesGroupedByStackNumbers[stackNumber] = stackMetadata[stackNumber].subGroupNames.filter(
                    (subGroupName) => subGroupNamesSet.has(subGroupName),
                );
            });

            constraints[comboKey][teamName] = {
                constraints: {
                    defensemanLineGroups: defensemanLineGroupConstraints,
                    defensemanPpLines: defensemanPpLineConstraints,
                    lineGroups: lineGroupConstraints,
                    ppLines: ppLineConstraints,
                    goalie: goalieConstraint,
                },
                subGroupNames: [...subGroupNamesSet],
                subGroupNamesGroupedByStackNumbers,
            };
        });
    });

    function createTeamStackMetadata(params) {
        const {combo, teamSimplexUnits, teamName} = params;
        const {lineGroupSimplexUnits, ppLineSimplexUnits} = teamSimplexUnits[teamName];

        const comboTeamMetadata = {};
        Object.keys(combo.playersQuantity).map((stackNumber) => {
            const playersQuantity = combo.playersQuantity[stackNumber];

            const lineGroupNames = {};
            Object.keys(lineGroupSimplexUnits).map((lineGroup) => {
                lineGroupNames[lineGroup] = getSubGroupName(teamName, combo.comboId, stackNumber, 'LG', lineGroup);
            });

            const ppLineNames = {};
            Object.keys(ppLineSimplexUnits).map((ppLine) => {
                ppLineNames[ppLine] = getSubGroupName(teamName, combo.comboId, stackNumber, 'PL', ppLine);
            });

            comboTeamMetadata[stackNumber] = {
                stackNumber,
                lineGroupNames,
                ppLineNames,
                playersQuantity,
                subGroupNames: [...Object.values(lineGroupNames), ...Object.values(ppLineNames)],
            };
        });

        return comboTeamMetadata;
    }

    function createDefensemanConstraints(params) {
        const {defensemanTeamSimplexUnits, teamSimplexUnits, combo, stackMetadata, groupName, groupNumber} = params;
        const rowPlayersQuantity = teamSimplexUnits.length;

        const constraints = [];
        if (isEmpty(defensemanTeamSimplexUnits)) {
            return constraints;
        }

        const atLeastSubGroupNames = [];
        const atMostSubGroupNames = [];
        Object.keys(stackMetadata).map((stackNumber) => {
            const metadata = stackMetadata[stackNumber];

            if (rowPlayersQuantity < metadata.playersQuantity) {
                return;
            }

            const subGroupName = metadata[groupName][groupNumber];

            if (stacking.isDefensemanMandatoryInCombinationStack(combo, stackNumber)) {
                atLeastSubGroupNames.push(subGroupName);
            }

            atMostSubGroupNames.push(subGroupName);
        });

        if (!isEmpty(atLeastSubGroupNames)) {
            const ruleBuilder = RuleBuilder.fromAtLeast(0);
            atLeastSubGroupNames.map((subGroupName) => ruleBuilder.addVar(subGroupName, -1));
            defensemanTeamSimplexUnits.map((simplexUnit) => ruleBuilder.addVar(simplexUnit, 1));

            constraints.push(ruleBuilder.createRule());
        }

        if (!isEmpty(atMostSubGroupNames)) {
            const playersInTeam = 10;

            const ruleBuilder = RuleBuilder.fromAtMost(playersInTeam);
            atMostSubGroupNames.map((subGroupName) => ruleBuilder.addVar(subGroupName, playersInTeam - 1));
            defensemanTeamSimplexUnits.map((simplexUnit) => ruleBuilder.addVar(simplexUnit, 1));

            constraints.push(ruleBuilder.createRule());
        }

        return {
            constraints,
            subGroupNames: [...atLeastSubGroupNames, ...atMostSubGroupNames],
        };
    }

    function createGoalieConstraint(params) {
        const {goalieSimplexUnits, combo, stackMetadata, subGroupNamesSet} = params;

        if (isEmpty(goalieSimplexUnits)) {
            return {};
        }

        const subGroupNames = [];
        Object.keys(combo.playersQuantity).map((stackNumber) => {
            const isGoalieAllowed = Number(combo.playersQuantity[stackNumber]) < 3;

            if (isGoalieAllowed) {
                return;
            }

            stackMetadata[stackNumber].subGroupNames
                .filter((subGroupName) => subGroupNamesSet.has(subGroupName))
                .map((subGroupName) => subGroupNames.push(subGroupName));
        });

        if (isEmpty(subGroupNames)) {
            return {};
        }

        const ruleBuilder = RuleBuilder.fromAtMost(1);
        goalieSimplexUnits.map((simplexUnit) => ruleBuilder.addVar(simplexUnit, 1));
        subGroupNames.map((subGroupName) => ruleBuilder.addVar(subGroupName, 1));

        return ruleBuilder.createRule();
    }

    function getSimplexTeamPlayerUnits(teamName, isPlayerValidFunc) {
        return (playersGroupedByTeams[teamName] || [])
            .filter((player) => isPlayerValidFunc(player))
            .map((player) => getSimplexUnitName(player.getId()));
    }

    return constraints;
}

function createConstraintsForExcludedTeamsByExposureMax(params) {
    const {constraints, lineupsQuantity, usedTeamNames, stacking} = params;

    let filteredConstraints = cp(constraints);

    Object.keys(constraints).map((teamName) => {
        const expMax = stacking.getTeamMaxExposureInStackNumber(teamName, 1);

        const usageQuantity = countEntries(teamName, usedTeamNames);
        const usagePercent = ((usageQuantity + 1) / lineupsQuantity.getLineupsQuantity()) * 100;

        if (usagePercent > expMax) {
            delete filteredConstraints[teamName];
        }
    });

    return filteredConstraints;
}

function calcCombinationsQuantity(params) {
    const {stacking, lineupsQuantity} = params;
    const combinations = stacking.getCheckedCombinations();

    const result = [];
    Object.keys(combinations).map((comboKey) => {
        const {exposure} = combinations[comboKey];

        const minLineupsQuantity = calcAndRoundUpValByPct(lineupsQuantity.getLineupsQuantity(), exposure.min);
        let maxLineupsQuantity = calcAndRoundDownValByPct(lineupsQuantity.getLineupsQuantity(), exposure.max);

        if (maxLineupsQuantity < minLineupsQuantity) {
            maxLineupsQuantity = minLineupsQuantity;
        }

        result.push({
            comboKey,
            minLineupsQuantity,
            maxLineupsQuantity,
        });
    });

    return [...result];
}

class ConstraintsBuilder {
    constructor(dependencies) {
        this.dependencies = {...dependencies};
    }

    createObjectiveForRandomlyChosenPlayers(params) {
        const {playersGroupedByTeams} = this.dependencies;
        const {constraints, projectionPointsPropName} = params;

        return createObjectiveForRandomlyChosenPlayers({
            constraints,
            playersGroupedByTeams,
            projectionPointsPropName,
        });
    }

    createConstraintsForExcludeTeamsCombinations(params) {
        return createConstraintsForExcludeTeamsCombinations(params);
    }

    createGroupsConstraints(params) {
        return createGroupsConstraints(params);
    }

    createConstraintsForIncludedTeamsByExposureMin(params) {
        const {stacking} = this.dependencies;

        return createConstraintsForIncludedTeamsByExposureMin({
            stacking,
            ...params,
        });
    }

    createConstraintsForExcludedTeamsByExposureMax(params) {
        const {stacking} = this.dependencies;

        return createConstraintsForExcludedTeamsByExposureMax({
            stacking,
            ...params,
        });
    }

    createDoubleTeamsConstraints(params) {
        return createDoubleTeamsConstraints(params);
    }

    createConstraintCombinations(params) {
        const {stacking, playersGroupedByTeams, sportConfig} = this.dependencies;

        return createConstraintCombinations({
            sportConfig,
            stacking,
            playersGroupedByTeams,
            ...params,
        });
    }

    calcCombinationsQuantity(params) {
        const {stacking} = this.dependencies;

        return calcCombinationsQuantity({stacking, ...params});
    }
}

export {ConstraintsBuilder};
